import { useEffect, useMemo, useRef, useState } from 'react'
import { Search, X, Loader2, Plus, Trash2, FileText, Undo2, Save, Store } from 'lucide-react'
import { apiGet, apiDelete, postJson } from '../../lib/api.js'
import { fetchInventoryMaster, rows } from '../../lib/stock.js'
import { relabelBranch } from '../../lib/ticket.js'
import {
  buildEditPayload, buildPoLinePayload, reqItemName, reqQty, reqUnit, fmtBaht,
  fetchProcurement,
} from '../../lib/purchase.js'
import QtyStepper from './QtyStepper.jsx'
import ConfirmDialog from '../ConfirmDialog.jsx'
import { useTypeahead } from '../../lib/useTypeahead.js'
import SellerPicker from './SellerPicker.jsx'
import PurchaseFiles from './PurchaseFiles.jsx'

// The purchase line editor. Two callers, one form:
//   • ตรวจสอบ (stage 2) — opened by clicking a PO number; edits the whole PO.
//   • คำขอซื้อ (stage 1) — opened by clicking a request row; the same drawer over
//     a one-line group with no PO, so `allowAdd` is off (a stage-1 line has no PO
//     to add into — use สร้างคำขอซื้อ instead).
//
// Everything is edited as a DRAFT and committed on บันทึก: nothing is written
// until then, so ยกเลิก is a true cancel and a half-finished edit can't leave the
// PO in a mixed state. On save we diff the draft against the loaded rows and
// write only what changed — deletes first, then edits, then the new lines.
//
// สาขา is PER LINE — it is the branch that NEEDS the item, so a PO that pools
// requests from several branches keeps each line's own. The branch that carries
// the PO's paperwork + expense is a different field entirely (procurement.
// branch_id), picked on the ตรวจสอบ card when the PO is sent to ชำระเงิน.
// The PO's own branch — the one that carries its paperwork and expense — is
// editable here too, but it is NOT part of the draft: Xano has nowhere to keep it
// until the PO advances (it becomes procurement.branch_id → expense.branch_id),
// so the page owns that value and this select writes to it straight away.
//
// วันที่ is the other PO-level field here; จำนวน / หมายเหตุ stay per line. The ITEM
// is fixed once a line exists — to change it, delete the line and add the right
// one, so a line's history never silently becomes a different product.
//
// Safe only in stage 2: from ชำระเงิน onward a `procurement` row (and later an
// expense + received stock) keys on these lines, so the caller must not open this
// for stages 3–5.

const lineKeyOf = (l) => (l.id > 0 ? `r${l.id}` : `n${l.tmp}`)
const FIELD_LABEL = 'block text-[11px] font-medium uppercase tracking-wide text-slate-400'

const branchLabel = (branches, id) => {
  const b = rows(branches).find((x) => Number(x.id) === Number(id))
  return relabelBranch(b?.name || b?.Name) || (id ? `สาขา ${id}` : '—')
}

function ReadRow({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <span className="text-[12.5px] text-slate-500 shrink-0">{label}</span>
      <span className="min-w-0 text-[13.5px] text-slate-800 truncate text-right">{children}</span>
    </div>
  )
}

export default function PurchaseEditDrawer({
  group, branches, focusLineId = 0, allowAdd = true, canDelete = true,
  poBranch = null, onPoBranchChange, showPrice = false, showSeller = false,
  showFiles = false, warnBookedExpense = false, readOnly = false, newLineStage,
  title, subtitle, onClose, onSaved, onDeleted,
}) {
  const original = useMemo(() => group?.rows || [], [group])

  const [date, setDate] = useState(group?.date || '')
  // ผู้ขาย lives on every line (inventory_request.seller_id) and is copied onto the
  // procurement + expense at 3→4, so it is a normal part of the draft: pick here,
  // บันทึก writes it to each line.
  const [seller, setSeller] = useState(
    group?.sellerId ? { id: group.sellerId, name: group.sellerName, code: group.sellerCode } : null)
  const [poBranchDraft, setPoBranchDraft] = useState(String(poBranch ?? ''))
  // The PO-level record. Absent (404) until something is saved against this PO —
  // that is the normal state for a PO still in ตรวจสอบ.
  const [proc, setProc] = useState(null)
  const [lines, setLines] = useState(() => original.map((r) => ({
    id: r.id,
    row: r,
    inventory_id: Number(r.inventory_id) || 0,
    name: reqItemName(r),
    unit: reqUnit(r),
    branch_id: String(r.branch_id || ''),
    price: r?.price == null || r.price === '' ? '' : String(r.price),
    qty: String(reqQty(r)),
    remark: r?.remark || '',
    deleted: false,
  })))
  const [adding, setAdding] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [confirmDeletePo, setConfirmDeletePo] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const [master, setMaster] = useState([])
  const [loadingMaster, setLoadingMaster] = useState(false)
  const tmpRef = useRef(0)
  const focusRef = useRef(null)

  // Opened from a line's แก้ไข button — bring that line into view.
  useEffect(() => { focusRef.current?.scrollIntoView({ block: 'center' }) }, [])

  // The item master is only needed once the user opens the add picker — it is a
  // multi-page fetch, so don't pay for it just to change a qty.
  useEffect(() => {
    if (!adding || master.length || loadingMaster) return
    let alive = true
    setLoadingMaster(true)
    fetchInventoryMaster(apiGet)
      .then((items) => { if (alive) setMaster(items) })
      .catch(() => { if (alive) setError('โหลดรายการของไม่สำเร็จ') })
      .finally(() => { if (alive) setLoadingMaster(false) })
    return () => { alive = false }
  }, [adding, master.length, loadingMaster])

  useEffect(() => {
    if (!showFiles || !group?.po) return
    let alive = true
    fetchProcurement(apiGet, group.po)
      .then((r) => { if (alive && r) { setProc(r); if (r.branch_id) setPoBranchDraft(String(r.branch_id)) } })
      .catch(() => {})
    return () => { alive = false }
  }, [showFiles, group?.po])

  const patchLine = (key, patch) =>
    setLines((ls) => ls.map((l) => (lineKeyOf(l) === key ? { ...l, ...patch } : l)))

  const pickItem = (it) => {
    tmpRef.current += 1
    setLines((ls) => [...ls, {
      id: 0, row: null, tmp: tmpRef.current,
      inventory_id: it.id, name: it.Name, unit: it.unit || '',
      branch_id: String(group?.branchId || ''),
      qty: '1', remark: '', price: '', deleted: false,
    }])
    setAdding(false)
  }

  const live = lines.filter((l) => !l.deleted)
  // วันที่ only counts as an edit once the user actually moves it — otherwise a
  // group whose lines carry different dates would show phantom changes the moment
  // the drawer opens. Untouched lines keep their own date.
  const poDateChanged = String(date || '') !== String(group?.date || '')
  const sellerChanged = Number(seller?.id || 0) !== Number(group?.sellerId || 0)
  const changedLines = lines.filter((l) => l.id > 0 && !l.deleted && (
    (sellerChanged && Number(seller?.id || 0) !== Number(l.row?.seller_id || 0)) ||
    Math.trunc(Number(l.qty) || 0) !== reqQty(l.row) ||
    String(l.remark || '') !== String(l.row?.remark || '') ||
    Number(l.branch_id) !== Number(l.row?.branch_id) ||
    (showPrice && String(l.price ?? '') !== String(l.row?.price ?? '')) ||
    (poDateChanged && String(date || '') !== String(l.row?.date || ''))
  ))
  const deletedLines = lines.filter((l) => l.id > 0 && l.deleted)
  const newLines = lines.filter((l) => l.id === 0 && !l.deleted)
  const poBranchChanged = poBranch != null && Number(poBranchDraft) !== Number(poBranch)
  const changeCount = changedLines.length + deletedLines.length + newLines.length + (poBranchChanged ? 1 : 0)

  const badQty = live.some((l) => !(Math.trunc(Number(l.qty) || 0) > 0))
  const badBranch = live.some((l) => !(Number(l.branch_id) > 0))
  const canSave = !saving && changeCount > 0 && !badQty && !badBranch

  // Deleting a line is a hard delete in Xano, so it gets a confirm step — but
  // only when the draft actually removes something; a pure qty/หมายเหตุ edit
  // saves straight through.
  const commit = () => (deletedLines.length ? setConfirming(true) : save())

  async function save() {
    if (!canSave) return
    setConfirming(false)
    setSaving(true); setError(null)
    try {
      for (const l of deletedLines) {
        await apiDelete(`/api/purchase?inventory_request_id=${l.id}`)
      }
      for (const l of changedLines) {
        await postJson('/api/purchase', buildEditPayload(l.row, {
          branch_id: Number(l.branch_id),
          N: Math.trunc(Number(l.qty) || 0),
          remark: l.remark || '',
          ...(showPrice ? { price: l.price === '' ? null : Number(l.price) } : {}),
          ...(sellerChanged ? { seller_id: Number(seller?.id || 0) } : {}),
          ...(poDateChanged ? { date } : {}),
        }))
      }
      const newIds = []
      for (const l of newLines) {
        const created = await postJson('/api/purchase', buildPoLinePayload({
          inventory_id: l.inventory_id,
          branch_id: l.branch_id,
          qty: l.qty,
          remark: l.remark,
          po: group.po,
          date: date || group?.date || undefined,
          stage: newLineStage,
        }))
        if (created?.id) newIds.push(created.id)
      }

      // Everything that belongs to the PO rather than to a line goes onto the
      // procurement record — created on demand, so a PO that never needed one
      // still doesn't get an empty row.
      if (group.po && (showSeller || showFiles) && (sellerChanged || poBranchChanged)) {
        await postJson('/api/procurement', {
          PO_number: group.po,
          ...(seller?.id ? { seller_id: Number(seller.id) } : {}),
          ...(Number(poBranchDraft) > 0 ? { branch_id: Number(poBranchDraft) } : {}),
          ...(date ? { date } : {}),
          inventory_request_id: [...live.filter((l) => l.id > 0).map((l) => l.id), ...newIds],
        })
      }
      if (poBranchChanged) onPoBranchChange?.(poBranchDraft)
      onSaved?.(`บันทึก${group.po ? ` ${group.po}` : ''}แล้ว — แก้ ${changedLines.length} · เพิ่ม ${newLines.length} · ลบ ${deletedLines.length}`)
    } catch (e) {
      setError(`${e.message || 'บันทึกไม่สำเร็จ'} — โหลดใหม่แล้วตรวจสอบก่อนบันทึกซ้ำ`)
    } finally {
      setSaving(false)
    }
  }

  // ลบ PO ทั้งใบ. Xano's DELETE /procurement/{PO_number} is the cascade: it drops
  // every request line under the PO, the PO record itself, any received stock
  // rows, and the linked expense — so nothing is left orphaned. One call, and it
  // is why deleting is worded as strongly as it is.
  async function deletePo() {
    setConfirmDeletePo(false)
    setSaving(true); setError(null)
    try {
      await apiDelete(`/api/purchase/${encodeURIComponent(group.po)}`)
      onDeleted?.(`ลบ ${group.po} และรายการทั้งหมดในใบนี้แล้ว`)
    } catch (e) {
      setError(e.message || 'ลบ PO ไม่สำเร็จ')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={() => !saving && onClose?.()} />
      <div className="relative w-full max-w-[560px] bg-slate-50 shadow-2xl flex flex-col animate-ss-slide-in">
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><FileText size={18} /></div>
            <div>
              <div className="text-[15.5px] font-semibold text-slate-900 tabular-nums">{title || group.po || '(ยังไม่มี PO)'}</div>
              <div className="text-[12.5px] text-slate-500">{subtitle || 'แก้ไขก่อนส่งไปชำระเงิน'}</div>
            </div>
          </div>
          <button onClick={() => !saving && onClose?.()} className="p-1.5 text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Past ชำระเงิน the money is already booked. Editing here is allowed —
              a wrong quantity is still worth fixing — but nothing rewrites the
              expense, so say so plainly instead of letting the two drift apart
              silently. */}
          {warnBookedExpense && proc?.expense_id ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12.5px] text-amber-900">
              PO นี้บันทึกใบค่าใช้จ่ายไว้แล้ว — แก้ไขที่นี่ <b>จะไม่แก้ใบค่าใช้จ่ายให้อัตโนมัติ</b>
              <div className="mt-1 text-amber-800/80">
                ถ้าต้องให้ยอดตรงกัน ให้แก้ที่หน้าค่าใช้จ่ายด้วย หรือส่ง PO กลับไปขั้นชำระเงินแล้วยืนยันใหม่ (ระบบจะอัปเดตใบเดิม)
              </div>
            </div>
          ) : null}
          {readOnly ? (
            /* Past ชำระเงิน the PO is settled — its quantities and prices are already
               in an expense and (once received) in the stock ledger, so editing
               them here would silently desync both. Attachments are the exception:
               delivery notes and receipts keep arriving after the fact. */
            <>
              <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                <ReadRow label="สาขาของ PO">{branchLabel(branches, poBranchDraft)}</ReadRow>
                {seller && <ReadRow label="ผู้ขาย">{seller.name}</ReadRow>}
                <ReadRow label="วันที่">{date || '—'}</ReadRow>
              </div>

              <div>
                <div className="text-[12.5px] font-medium text-slate-500 mb-1.5">รายการ ({live.length})</div>
                <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                  {live.map((l) => (
                    <div key={lineKeyOf(l)} className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1 text-[13.5px] text-slate-800 truncate">{l.name}</div>
                        <span className="text-[13px] tabular-nums text-slate-700 whitespace-nowrap">{l.qty} {l.unit}</span>
                        {showPrice && (
                          <span className="w-24 text-right text-[13px] tabular-nums text-slate-500">
                            {l.price === '' ? '—' : `฿${fmtBaht(Number(l.price))}`}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11.5px] text-slate-400">
                        <span>{branchLabel(branches, l.branch_id)}</span>
                        {l.remark && <span className="truncate italic">“{l.remark}”</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
          <>
          {/* PO-level fields */}
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 space-y-3">
            {poBranch != null && (
              <label className="block">
                <span className="text-[12.5px] font-medium text-slate-500">สาขาของ PO (เอกสาร / ค่าใช้จ่าย)</span>
                <select value={poBranchDraft} onChange={(e) => setPoBranchDraft(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800">
                  {rows(branches).map((b) => (
                    <option key={b.id} value={b.id}>{relabelBranch(b.name || b.Name) || `สาขา ${b.id}`}</option>
                  ))}
                </select>
                <span className="mt-1 block text-[11.5px] text-slate-400">
                  สาขาที่ขอของอยู่ที่แต่ละรายการด้านล่าง
                </span>
              </label>
            )}
            {showSeller && (
              <div>
                <span className="text-[12.5px] font-medium text-slate-500">ผู้ขาย</span>
                <div className="mt-1">
                  {seller ? (
                    <span title={seller.name}
                      className="flex items-center gap-1.5 rounded-lg bg-violet-50 border border-violet-200 px-2.5 py-1.5 text-[13.5px] text-violet-800">
                      <Store size={14} className="shrink-0" />
                      <span className="min-w-0 flex-1 truncate">{seller.name}</span>
                      <button onClick={() => setSeller(null)} title="เปลี่ยนผู้ขาย"
                        className="text-violet-400 hover:text-violet-700"><X size={13} /></button>
                    </span>
                  ) : (
                    <SellerPicker width="w-full" autoFocus onPick={(sel) => setSeller({ id: sel.id, name: sel.name, code: sel.code })} />
                  )}
                </div>
                <span className="mt-1 block text-[11.5px] text-slate-400">ต้องเลือกก่อนยืนยันการชำระเงิน</span>
              </div>
            )}
            <label className="block">
              <span className="text-[12.5px] font-medium text-slate-500">วันที่</span>
              <input type="date" value={date || ''} onChange={(e) => setDate(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800" />
            </label>
          </div>

          {/* lines */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[12.5px] font-medium text-slate-500">รายการ ({live.length})</span>
              {allowAdd && (
                <button onClick={() => setAdding((v) => !v)}
                  className="text-[12.5px] text-brand-600 hover:text-brand-700 flex items-center gap-1">
                  <Plus size={13} />เพิ่มของเข้า PO นี้
                </button>
              )}
            </div>

            {adding && (
              <ItemPicker
                master={master} loading={loadingMaster} onPick={pickItem} onClose={() => setAdding(false)}
                exclude={live.map((l) => l.inventory_id)} label="ค้นหาของที่จะเพิ่ม"
              />
            )}

            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
              {lines.map((l) => {
                const key = lineKeyOf(l)
                if (l.deleted) {
                  return (
                    <div key={key} className="flex items-center gap-3 px-4 py-2.5 bg-rose-50/40">
                      <div className="min-w-0 flex-1 text-[13.5px] text-rose-400 line-through truncate">{l.name}</div>
                      <button onClick={() => patchLine(key, { deleted: false })}
                        className="text-[12.5px] text-slate-500 hover:text-slate-700 flex items-center gap-1"><Undo2 size={13} />เลิกลบ</button>
                    </div>
                  )
                }
                const focused = l.id > 0 && Number(l.id) === Number(focusLineId)
                return (
                  <div key={key} ref={focused ? focusRef : null}
                    className={`px-4 py-3 ${focused ? 'bg-brand-50/50 ring-1 ring-inset ring-brand-200' : ''}`}>
                    {/* The item name owns the first line and wraps in full — it is
                        what identifies the row. Everything editable sits below it
                        in a labelled two-column grid, so the controls read as
                        fields rather than as a row of bare inputs. */}
                    <div className="flex items-start gap-2 mb-2">
                      <div className="min-w-0 flex-1 text-[13.5px] text-slate-800 leading-snug break-words">
                        {l.name}
                        {l.id === 0 && <span className="ml-2 align-middle text-[11px] text-emerald-600">ใหม่</span>}
                      </div>
                      {(canDelete || l.id === 0) && (
                        <button onClick={() => l.id > 0 ? patchLine(key, { deleted: true }) : setLines((ls) => ls.filter((x) => lineKeyOf(x) !== key))}
                          title="ลบรายการ" className="shrink-0 p-1 -m-1 text-slate-300 hover:text-rose-600"><Trash2 size={15} /></button>
                      )}
                    </div>

                    {/* จำนวน · ราคารวม · สาขาที่ขอ share one row — they are the
                        three facts about the line. flex-wrap so a narrow window
                        drops them onto a second row instead of crushing the
                        select. หมายเหตุ is free text, so it gets its own width. */}
                    <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
                      <div className="shrink-0">
                        <span className={FIELD_LABEL}>จำนวน</span>
                        <div className="mt-1 flex items-center gap-2">
                          <QtyStepper value={l.qty} onChange={(v) => patchLine(key, { qty: v })} />
                          {l.unit && <span className="text-[12px] text-slate-400 truncate max-w-[60px]">{l.unit}</span>}
                        </div>
                      </div>

                      {showPrice && (
                        <div className="w-[110px] shrink-0">
                          <span className={FIELD_LABEL}>ราคารวม (฿)</span>
                          <input value={l.price} inputMode="decimal" placeholder="0.00"
                            onChange={(e) => patchLine(key, { price: e.target.value.replace(/[^\d.]/g, '') })}
                            className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-[13px] text-right tabular-nums text-slate-800" />
                          {Number(l.price) > 0 && Math.trunc(Number(l.qty) || 0) > 0 && (
                            <span className="mt-0.5 block text-right text-[11px] text-slate-400 tabular-nums">
                              ฿{fmtBaht(Number(l.price) / Math.trunc(Number(l.qty)))}/หน่วย
                            </span>
                          )}
                        </div>
                      )}

                      <div className="flex-1 min-w-[140px]">
                        <span className={FIELD_LABEL}>สาขาที่ขอ</span>
                        <select value={l.branch_id} onChange={(e) => patchLine(key, { branch_id: e.target.value })}
                          title="สาขาที่ต้องการใช้ของชิ้นนี้"
                          className={`mt-1 w-full rounded-lg border bg-white px-2 py-1.5 text-[12.5px] ${
                            Number(l.branch_id) > 0 ? 'border-slate-200 text-slate-700' : 'border-rose-300 bg-rose-50 text-rose-700'}`}>
                          <option value="">— เลือกสาขา —</option>
                          {rows(branches).map((b) => (
                            <option key={b.id} value={b.id}>{relabelBranch(b.name || b.Name) || `สาขา ${b.id}`}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mt-2">
                      <span className={FIELD_LABEL}>หมายเหตุ</span>
                      <input value={l.remark} onChange={(e) => patchLine(key, { remark: e.target.value })}
                        placeholder="ไม่บังคับ"
                        className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12.5px] text-slate-700 placeholder:text-slate-300" />
                    </div>
                  </div>
                )
              })}
              {lines.length === 0 && (
                <div className="px-4 py-8 text-center text-[13px] text-slate-400">ไม่มีรายการใน PO นี้</div>
              )}
            </div>
            {live.length === 0 && lines.length > 0 && (
              <p className="mt-1.5 text-[12px] text-rose-600">
                {group.po ? 'ลบทุกรายการแล้ว — บันทึกจะทำให้ PO นี้หายไปทั้งใบ' : 'บันทึกจะลบคำขอนี้ทิ้ง'}
              </p>
            )}
          </div>

          </>
          )}

          {showFiles && group.po && (
            <PurchaseFiles po={group.po} files={proc?.file || []}
              onChange={(f) => setProc((prev) => ({ ...(prev || {}), file: f }))} />
          )}

          {canDelete && !readOnly && group.po && (
            <div className="rounded-xl border border-rose-200 bg-rose-50/40 px-4 py-3">
              <div className="text-[12.5px] font-medium text-rose-800">ลบ PO ทั้งใบ</div>
              <p className="mt-0.5 text-[11.5px] text-rose-700/80">
                ลบทั้งใบพร้อมรายการทั้งหมดใน PO นี้ ({live.length} รายการ) — ไม่เหลือรายการค้างในระบบ
              </p>
              <button onClick={() => setConfirmDeletePo(true)} disabled={saving}
                className="mt-2 rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-[12.5px] font-medium text-rose-700 hover:bg-rose-600 hover:text-white hover:border-rose-600 disabled:opacity-40 flex items-center gap-1.5">
                <Trash2 size={13} />ลบ {group.po}
              </button>
            </div>
          )}

          {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">{error}</div>}
        </div>

        {/* footer */}
        <div className="px-5 py-4 bg-white border-t border-slate-200 flex items-center justify-between gap-3">
          <span className="text-[12.5px] text-slate-400">
            {readOnly ? 'แก้ไขได้เฉพาะเอกสารแนบ'
              : changeCount === 0 ? 'ยังไม่มีการแก้ไข'
              : `แก้ ${changedLines.length} · เพิ่ม ${newLines.length} · ลบ ${deletedLines.length}`}
          </span>
          <div className="flex items-center gap-3">
            <button onClick={() => !saving && onClose?.()} className="rounded-xl px-4 py-2 text-[14px] text-slate-600 hover:bg-slate-100">{readOnly ? 'ปิด' : 'ยกเลิก'}</button>
            {!readOnly && <button onClick={commit} disabled={!canSave}
              className="rounded-xl bg-brand-600 px-5 py-2 text-[14px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40 flex items-center gap-2">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}บันทึก
            </button>}
          </div>
        </div>
      </div>

      {confirmDeletePo && (
        <ConfirmDialog
          tone="danger"
          title={`ลบ ${group.po} ทั้งใบ?`}
          confirmLabel="ลบทั้งใบ"
          onCancel={() => setConfirmDeletePo(false)}
          onConfirm={deletePo}
          message={
            <>
              <div>สิ่งที่จะถูกลบถาวร กู้คืนไม่ได้:</div>
              <ul className="mt-2 space-y-1 text-[13px] text-slate-700">
                <li>· รายการทั้งหมดใน PO นี้ ({live.length} รายการ)</li>
                <li>· ตัว PO เอง</li>
                {proc?.expense_id ? <li className="text-rose-700 font-medium">· ใบค่าใช้จ่ายที่ผูกกับ PO นี้</li> : null}
                {proc?.file?.length ? <li>· การอ้างอิงเอกสารแนบ {proc.file.length} ไฟล์</li> : null}
              </ul>
              <div className="mt-2 text-[12.5px] text-slate-500">รวมถึงรายการรับของที่บันทึกไว้แล้ว (ถ้ามี)</div>
            </>
          }
        />
      )}

      {confirming && (
        <ConfirmDialog
          tone="danger"
          title={`ลบ ${deletedLines.length} รายการถาวร?`}
          confirmLabel="ลบและบันทึก"
          onCancel={() => setConfirming(false)}
          onConfirm={save}
          message={
            <>
              <div>รายการต่อไปนี้จะถูกลบออกจากระบบ กู้คืนไม่ได้</div>
              <ul className="mt-2 space-y-1">
                {deletedLines.map((l) => (
                  <li key={lineKeyOf(l)} className="flex items-center gap-2 text-[13.5px] text-slate-700">
                    <Trash2 size={13} className="text-rose-400 shrink-0" />
                    <span className="truncate">{l.name}</span>
                    <span className="text-slate-400 tabular-nums shrink-0">{reqQty(l.row)} {l.unit}</span>
                  </li>
                ))}
              </ul>
              {group.po && live.length === 0 && (
                <div className="mt-3 rounded-lg bg-rose-50 border border-rose-100 px-3 py-2 text-[13px] text-rose-800">
                  นี่เป็นรายการสุดท้ายของ {group.po} — PO นี้จะหายไปทั้งใบ
                </div>
              )}
              {(changedLines.length > 0 || newLines.length > 0) && (
                <div className="mt-3 text-[13px] text-slate-500">
                  และจะบันทึกการแก้ไขอีก {changedLines.length + newLines.length} รายการพร้อมกัน
                </div>
              )}
            </>
          }
        />
      )}
    </div>
  )
}

/* ── item picker — the same search-the-master affordance as สร้างคำขอซื้อ ─────── */
function ItemPicker({ master, loading, onPick, onClose, exclude = [], label }) {
  const [q, setQ] = useState('')
  const ref = useRef(null)
  useEffect(() => { ref.current?.focus() }, [])
  const taken = new Set(exclude.map(Number))
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return []
    return master.filter((it) => String(it.Name || '').toLowerCase().includes(needle)).slice(0, 30)
  }, [q, master])

  // Items already on the PO are shown but not pickable, so Enter must skip them
  // rather than land on a disabled row.
  const pick = (it) => { if (!taken.has(Number(it.id))) onPick(it) }
  const ta = useTypeahead({
    count: results.length,
    onSelect: (i) => { const it = results[i]; if (it) pick(it) },
    onClose,
  })

  return (
    <div className="mb-2 rounded-xl border border-brand-200 bg-brand-50/40 p-2.5">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[12px] text-slate-500">{label}</span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
      </div>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input ref={ref} value={q} onChange={(e) => setQ(e.target.value)} disabled={loading} onKeyDown={ta.onKeyDown}
          placeholder={loading ? 'กำลังโหลดรายการของ…' : 'พิมพ์ชื่อของ…'}
          className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-1.5 text-[13.5px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-300" />
      </div>
      {q && (
        <div ref={ta.listRef} className="mt-1.5 rounded-lg border border-slate-200 bg-white divide-y divide-slate-100 max-h-[220px] overflow-y-auto">
          {results.length === 0 && <div className="px-3 py-2.5 text-[13px] text-slate-400">ไม่พบของที่ตรงกับ “{q}”</div>}
          {results.map((it, i) => (
            <button key={it.id} data-ti-index={i}
              onMouseEnter={() => ta.setActiveIndex(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(it) }}
              disabled={taken.has(Number(it.id))}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                i === ta.activeIndex ? 'bg-brand-50' : 'hover:bg-brand-50'}`}>
              <div className="min-w-0">
                <div className="text-[13px] text-slate-800 truncate">{it.Name}</div>
                <div className="text-[11px] text-slate-400 truncate">{[it.mainCategory, it.subCategory].filter(Boolean).join(' · ')}</div>
              </div>
              {taken.has(Number(it.id))
                ? <span className="text-[11.5px] text-brand-600 shrink-0">อยู่ใน PO แล้ว</span>
                : <Plus size={15} className="text-brand-500 shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
